'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import Accordion from '../common/Accordion';
import MuxPlayer from '@mux/mux-player-react';
import Link from 'next/link';

interface ProcessItem {
  text: string;
}

interface ProcessSectionItem {
  button_text: string;
  button_url?: string | null;
  link?: string | null;
}

interface Data {
  Process?: ProcessItem[];
  about_the_process: {
    process_section?: ProcessSectionItem[];
    link: any;
  };
  __typename: string;
  heading: string;
  bg_image: { url: string };
  url: any;
  media?: {
    enable_mux_video?: boolean;
    mux_video?: {
      playback_id?: string;
    };
    desktop_media?: { url: string }[];
  };
  padding: {
    mobile_top_padding: any;
    mobile_bottom_padding: any;
    desktop_top_padding: any;
    desktop_bottom_padding: any;
  };
}

interface AccordionItem {
  number: string;
  title: string;
  link: string;
}

interface ProcessSectionProps {
  data: Data;
}

const ComponentSectionsProcess = ({ data }: ProcessSectionProps) => {
  const processTexts = data.Process?.map((item) => item.text) || [];
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');
  // const romanNumerals = ['i', 'ii', 'iii'];
  const romanNumerals = (num: number): string => {
    const romans: [string, number][] = [
      ['m', 1000],
      ['cm', 900],
      ['d', 500],
      ['cd', 400],
      ['c', 100],
      ['xc', 90],
      ['l', 50],
      ['xl', 40],
      ['x', 10],
      ['ix', 9],
      ['v', 5],
      ['iv', 4],
      ['i', 1],
    ];
    let result = '';
    for (const [letter, value] of romans) {
      while (num >= value) {
        result += letter;
        num -= value;
      }
    }
    return result;
  };
  const accordionItems: AccordionItem[] =
    data.about_the_process.process_section?.map((item, index) => ({
      number: romanNumerals(index + 1),
      title: item.button_text,
      link: `/process/${item.button_url?.trim() || ''}`,
    })) || [];

  const muxPlaybackId = data?.media?.mux_video?.playback_id;
  const enableMuxVideo = data?.media?.enable_mux_video;

  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <section
      className="relative pb-8 px-4 md:px-6 h-screen"
      id={data?.__typename}
      style={{
        paddingBottom: `${bottom}rem`,
      }}
    >
      {/* Background video or image */}
      {enableMuxVideo && muxPlaybackId ? (
        <div className="absolute inset-0 z-0 overflow-hidden h-full">
          {/* <MuxPlayer
            autoPlay
            muted
            loop
            playsInline
            streamType="on-demand"
            className="w-full h-full object-cover"
            style={{ aspectRatio: '16 / 9', width: '100%' }}
          /> */}
          <MuxPlayer
            playbackId={muxPlaybackId}
            autoPlay
            muted
            loop
            playsInline
            streamType="on-demand"
            className="h-screen"
            style={{ aspectRatio: '16 / 9', width: '100%' }}
            // className="h-full object-cover w-full md:aspect-video"
          />
          <div className="absolute inset-0 bg-black opacity-30 z-10" />
        </div>
      ) : (
        <>
          {data.media?.desktop_media?.[0]?.url && (
            <>
              <Image
                src={data.media.desktop_media[0].url}
                alt="Media"
                fill
                className="object-cover z-0"
                style={{ position: 'absolute' }}
              />
              <div className="absolute inset-0 bg-black opacity-30 z-10" />
            </>
          )}
        </>
      )}

      {/* Content */}
      <div className="container-main text-[#e5e1d5] relative gap-8 z-20 h-full flex flex-col justify-between">
        {/* Sticky top content */}
        <div   
        style={{
        paddingTop: `${top}rem`,
      }}
       className="top md:sticky top-0 left-0 w-full z-10 pt-6 md:pt-8 md:h-[40.125rem]">
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 xl:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-[20px] md:text-[26px] font-light mb-7 md:mb-0 leading-none">
                {data.heading}
              </h2>
            </motion.div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:gap-12 items-start home-paragraph">
                {processTexts.map((text, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    className="inn-col"
                  >
                    <p
                      className="text-[16px] md:text-[14px] xl:text-[16px] font-light leading-[23px] md:leading-[20px] xl:leading-[23px] indent-10 text-cream"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom content with padding to avoid overlapping sticky header */}
        <div className="relative z-10 h-full">
          <div className="bottom grid grid-cols-1 md:grid-cols-2 md:gap-4 xl:gap-16 md:items-end h-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Link
                href={data.about_the_process.link.button_url}
                className="text-[16px] font-light text-[#e5e1d5] flex items-center"
              >
                {data.about_the_process.link.button_text}
                <Image
                  src="/images/img_frame_659.svg"
                  alt="Arrow"
                  width={18}
                  height={14}
                  className="ml-2"
                />
              </Link>
            </motion.div>

            <div className="space-y-6 mt-auto">
              <motion.div
                className="ml-auto"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Accordion items={accordionItems} />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComponentSectionsProcess;
